import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api, ApiError } from '@/api/client';
import { verificationApi } from '@/api/verification';
import type { InstitutionUser } from '@/types';
import {
  CREDENTIAL_TYPES,
  type CredentialType,
  type VerificationCredential,
} from '@/types/verification';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { KebabMenu } from '@/components/ui/Menu';
import { Icon } from '@/components/icons';
import { CredentialCardDialog } from '@/components/verification/CredentialCardDialog';
import { formatDateTime } from '@/utils/format';
import styles from './AdminCredentialsPage.module.css';

/**
 * The credentials console — issuing and governing the cards and mobile
 * identities that prove who someone is at a gate.
 *
 * WHAT THE SERVER CAN AND CANNOT DO TODAY shapes this page. The identity
 * service's GET /verification/credentials lists the CALLER'S OWN credentials —
 * there is no institution-wide or per-person listing endpoint yet. So the page
 * is built around the operations that do exist: issue (person picker → new
 * credential), the lifecycle actions on a credential id, and the printable card
 * face. Credentials issued in this session are kept on screen with their
 * holder's name; the server-listed panel is labelled honestly as the signed-in
 * account's own credentials rather than pretending to be a register.
 */

const NS = 'credentials';

type ActionKind = 'suspend' | 'reactivate' | 'revoke' | 'replace';

interface PendingAction {
  kind: ActionKind;
  credential: VerificationCredential;
  personName: string;
}

interface IssuedEntry {
  credential: VerificationCredential;
  personName: string;
}

export function AdminCredentialsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const institutionName = activeContext?.institution?.name ?? '';
  const locale = (i18n.language || 'en').split('-')[0];

  const { state: usersState } = useAsyncData<InstitutionUser[]>(
    `admin-users-${institutionId}`,
    () => api.admin.users.list(institutionId),
    !!institutionId,
  );
  const users = usersState.status === 'ready' ? usersState.data : [];

  const { state: ownState, reload: reloadOwn } = useAsyncData<VerificationCredential[]>(
    'verification-own-credentials',
    () => verificationApi.credentials.list(),
    true,
  );

  /** Credentials issued while this page has been open, newest first. */
  const [issued, setIssued] = useState<IssuedEntry[]>([]);
  const [issueOpen, setIssueOpen] = useState(false);
  const [action, setAction] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [replaceType, setReplaceType] = useState<CredentialType>('PHYSICAL_CARD');
  const [preview, setPreview] = useState<{ credential: VerificationCredential; personName: string } | null>(null);

  /**
   * The person picker offers institution members the way AdminUsersPage lists
   * them. The identity-service personId the issue endpoint needs is the row's
   * identityId (the same field api.admin.roles.assign uses); members whose
   * account is not linked to a person yet cannot be issued a credential and are
   * offered disabled rather than hidden, so the gap is visible.
   */
  const personOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.identityId ?? '',
        label: user.name || user.email,
        description: user.identityId
          ? `${user.roleName}${user.referenceNumber ? ` · ${user.referenceNumber}` : ''}`
          : t(`${NS}.issue.noIdentity`),
        disabled: !user.identityId,
      })),
    [users, t],
  );

  const nameForPerson = (personId: string | null): string => {
    if (!personId) return '—';
    const user = users.find((candidate) => candidate.identityId === personId);
    return user ? user.name || user.email : '—';
  };

  const updateIssued = (next: VerificationCredential) => {
    setIssued((entries) =>
      entries.map((entry) =>
        entry.credential.id === next.id ? { ...entry, credential: next } : entry,
      ),
    );
  };

  const runAction = async (pending: PendingAction, reason: string) => {
    setActionBusy(true);
    setActionError(null);
    try {
      let result: VerificationCredential;
      if (pending.kind === 'suspend') {
        result = await verificationApi.credentials.suspend(pending.credential.id, reason);
      } else if (pending.kind === 'revoke') {
        result = await verificationApi.credentials.revoke(pending.credential.id, reason);
      } else if (pending.kind === 'reactivate') {
        // The reactivate endpoint takes no body; the typed reason is the
        // operator's deliberation gate here, not a transmitted field.
        result = await verificationApi.credentials.reactivate(pending.credential.id);
      } else {
        // Replace answers with the SUCCESSOR credential (the old one is revoked
        // server-side in the same transaction), so it joins the issued list as
        // a new row rather than overwriting the old one.
        result = await verificationApi.credentials.replace(pending.credential.id, {
          credentialType: replaceType,
        });
      }

      if (pending.kind === 'replace') {
        setIssued((entries) => [
          { credential: result, personName: pending.personName },
          ...entries.map((entry) =>
            entry.credential.id === pending.credential.id
              ? { ...entry, credential: { ...entry.credential, status: 'REPLACED' } }
              : entry,
          ),
        ]);
      } else {
        updateIssued(result);
      }
      reloadOwn();
      toast.success(t(`${NS}.actions.${pending.kind}Done`));
      setAction(null);
    } catch (cause) {
      // 4xx sentences from the identity service are shown verbatim — the server
      // wrote them for exactly this operator.
      setActionError(
        cause instanceof ApiError || cause instanceof Error
          ? cause.message
          : t(`${NS}.actions.failed`),
      );
    } finally {
      setActionBusy(false);
    }
  };

  const menuFor = (credential: VerificationCredential, personName: string) => [
    {
      label: t(`${NS}.actions.card`),
      onClick: () => setPreview({ credential, personName }),
    },
    {
      label: t(`${NS}.actions.suspend`),
      onClick: () => openAction('suspend', credential, personName),
      disabled: credential.status !== 'ACTIVE',
    },
    {
      label: t(`${NS}.actions.reactivate`),
      onClick: () => openAction('reactivate', credential, personName),
      disabled: credential.status !== 'SUSPENDED',
    },
    {
      label: t(`${NS}.actions.replace`),
      onClick: () => openAction('replace', credential, personName),
      disabled: credential.status !== 'ACTIVE' && credential.status !== 'SUSPENDED',
    },
    {
      label: t(`${NS}.actions.revoke`),
      onClick: () => openAction('revoke', credential, personName),
      danger: true,
      disabled: credential.status === 'REVOKED' || credential.status === 'REPLACED',
    },
  ];

  const openAction = (kind: ActionKind, credential: VerificationCredential, personName: string) => {
    setActionError(null);
    setReplaceType(
      (CREDENTIAL_TYPES as readonly string[]).includes(credential.credentialType)
        ? (credential.credentialType as CredentialType)
        : 'PHYSICAL_CARD',
    );
    setAction({ kind, credential, personName });
  };

  const renderRows = (rows: IssuedEntry[]) => (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t(`${NS}.table.person`)}</th>
            <th>{t(`${NS}.table.reference`)}</th>
            <th>{t(`${NS}.table.type`)}</th>
            <th>{t(`${NS}.table.status`)}</th>
            <th>{t(`${NS}.table.issued`)}</th>
            <th>{t(`${NS}.table.expires`)}</th>
            <th>
              <span className={styles.srOnly}>{t(`${NS}.table.actions`)}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ credential, personName }) => (
            <tr key={credential.id}>
              <td>{personName}</td>
              <td>
                <span className={styles.code}>{credential.credentialPublicId}</span>
              </td>
              <td>
                {t(`${NS}.type.${credential.credentialType}`, {
                  defaultValue: credential.credentialType,
                })}
              </td>
              <td>
                <StateBadge kind="credential" value={credential.status} size="sm" />
              </td>
              <td>{formatDateTime(credential.issuedAt, locale)}</td>
              <td>
                {credential.expiresAt ? (
                  formatDateTime(credential.expiresAt, locale)
                ) : (
                  <span className={styles.cellMuted}>{t(`${NS}.table.noExpiry`)}</span>
                )}
              </td>
              <td>
                <div className={styles.rowActions}>
                  <KebabMenu
                    items={menuFor(credential, personName)}
                    ariaLabel={t(`${NS}.table.actionsFor`, {
                      reference: credential.credentialPublicId,
                    })}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t(`${NS}.title`)}</h1>
          <p className={styles.headerSubtitle}>{t(`${NS}.subtitle`)}</p>
        </div>
        <button type="button" className={styles.primaryAction} onClick={() => setIssueOpen(true)}>
          {t(`${NS}.issue.open`)}
        </button>
      </header>

      {/* The honest capability note: no institution-wide credential register
          exists server-side yet, and pretending otherwise would show operators
          an empty list and call it the truth. */}
      <div className={styles.unavailable} role="note">
        <span className={styles.unavailableIcon} aria-hidden="true">
          <Icon name="info" size={18} />
        </span>
        <div>
          <p className={styles.unavailableTitle}>{t(`${NS}.scopeNote.title`)}</p>
          <p className={styles.unavailableBody}>{t(`${NS}.scopeNote.body`)}</p>
        </div>
      </div>

      <div className={styles.section}>
        <Panel title={t(`${NS}.issuedPanel.title`)} icon="role" meta={issued.length || undefined}>
          {issued.length === 0 ? (
            <p className={styles.cellMuted}>{t(`${NS}.issuedPanel.empty`)}</p>
          ) : (
            renderRows(issued)
          )}
        </Panel>
      </div>

      <div className={styles.section}>
        <Panel title={t(`${NS}.ownPanel.title`)} icon="profile">
          <AsyncPanel
            state={ownState}
            onRetry={reloadOwn}
            isEmpty={(rows) => rows.length === 0}
            emptyTitle={t(`${NS}.ownPanel.empty`)}
            emptyBody={t(`${NS}.ownPanel.emptyBody`)}
          >
            {(rows) =>
              renderRows(
                rows.map((credential) => ({
                  credential,
                  personName: t(`${NS}.ownPanel.self`),
                })),
              )
            }
          </AsyncPanel>
        </Panel>
      </div>

      <IssueCredentialDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        institutionId={institutionId}
        personOptions={personOptions}
        onIssued={(credential, personId) => {
          setIssued((entries) => [{ credential, personName: nameForPerson(personId) }, ...entries]);
          toast.success(
            t(`${NS}.issue.done`, { reference: credential.credentialPublicId }),
          );
          setPreview({ credential, personName: nameForPerson(personId) });
        }}
      />

      {action && (
        <ConfirmActionDialog
          open
          title={t(`${NS}.actions.${action.kind}Title`)}
          intro={t(`${NS}.actions.${action.kind}Intro`, {
            name: action.personName,
            reference: action.credential.credentialPublicId,
          })}
          tone={action.kind === 'revoke' ? 'critical' : action.kind === 'suspend' ? 'warning' : 'neutral'}
          // The impact of a credential action is fully described by the row the
          // operator is looking at; there is no separate impact endpoint.
          impact={
            <p className={styles.unavailableBody}>
              {t(`${NS}.actions.impact`, {
                name: action.personName,
                reference: action.credential.credentialPublicId,
                status: action.credential.status,
              })}
            </p>
          }
          impactStatus="ready"
          extraFields={
            action.kind === 'replace' ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel} id="credentials-replace-type-label">
                  {t(`${NS}.issue.typeLabel`)}
                </span>
                <Select
                  aria-labelledby="credentials-replace-type-label"
                  value={replaceType}
                  onChange={(value) => setReplaceType(value as CredentialType)}
                  options={CREDENTIAL_TYPES.map((type) => ({
                    value: type,
                    label: t(`${NS}.type.${type}`),
                  }))}
                />
              </div>
            ) : undefined
          }
          confirmLabel={t(`${NS}.actions.${action.kind}Confirm`)}
          submitting={actionBusy}
          error={actionError}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => void runAction(action, reason)}
        />
      )}

      <CredentialCardDialog
        open={preview !== null}
        onClose={() => setPreview(null)}
        credential={preview?.credential ?? null}
        personName={preview?.personName ?? '—'}
        institutionName={institutionName}
      />
    </div>
  );
}

interface IssueCredentialDialogProps {
  open: boolean;
  onClose: () => void;
  institutionId: string;
  personOptions: { value: string; label: string; description?: string; disabled?: boolean }[];
  onIssued: (credential: VerificationCredential, personId: string) => void;
}

function IssueCredentialDialog({
  open,
  onClose,
  institutionId,
  personOptions,
  onIssued,
}: IssueCredentialDialogProps) {
  const { t } = useTranslation();
  const [personId, setPersonId] = useState('');
  const [credentialType, setCredentialType] = useState<CredentialType>('PHYSICAL_CARD');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!personId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const credential = await verificationApi.credentials.issue({
        personId,
        institutionId,
        credentialType,
        // A bare YYYY-MM-DD from the date input is valid ISO 8601, which is
        // exactly what the DTO validates; no timezone invention here.
        ...(expiresAt ? { expiresAt } : {}),
      });
      onIssued(credential, personId);
      setPersonId('');
      setExpiresAt('');
      setCredentialType('PHYSICAL_CARD');
      onClose();
    } catch (cause) {
      setError(
        cause instanceof ApiError || cause instanceof Error
          ? cause.message
          : t('credentials.issue.failed'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('credentials.issue.title')}
      description={t('credentials.issue.intro')}
    >
      <form
        className={styles.dialogBody}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className={styles.field}>
          <span className={styles.fieldLabel} id="credentials-issue-person-label">
            {t('credentials.issue.personLabel')}
          </span>
          <Select
            aria-labelledby="credentials-issue-person-label"
            value={personId}
            onChange={setPersonId}
            options={personOptions}
            placeholder={t('credentials.issue.personPlaceholder')}
            searchable
            searchPlaceholder={t('credentials.issue.personSearch')}
            emptyLabel={t('credentials.issue.personNone')}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel} id="credentials-issue-type-label">
            {t('credentials.issue.typeLabel')}
          </span>
          <Select
            aria-labelledby="credentials-issue-type-label"
            value={credentialType}
            onChange={(value) => setCredentialType(value as CredentialType)}
            options={CREDENTIAL_TYPES.map((type) => ({
              value: type,
              label: t(`credentials.type.${type}`),
            }))}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="credentials-issue-expiry">
            {t('credentials.issue.expiryLabel')}
          </label>
          <p className={styles.fieldHint}>{t('credentials.issue.expiryHint')}</p>
          <input
            id="credentials-issue-expiry"
            className={styles.textInput}
            type="date"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </div>

        {error && (
          <p className={styles.dialogError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={busy}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!personId || busy}>
            {busy ? t('credentials.issue.submitting') : t('credentials.issue.confirm')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
